
import { computed, defineComponent, onBeforeMount, PropType, ref } from 'vue'

// @types
import { Sector } from '@/new-services/types/sectors'
import { Courier } from '@/new-services/types/couriers'

// @components
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'

// @composable
import useAccount from '@/common/composable/useAccount'
import useNotifications from '@/common/composable/useNotifications'

// @services
import sectorsAPI from '@/new-services/sectors.service'

export default defineComponent({
    name: 'sector-list-item',
    components: {
        NewSelect,
        NewButton
    },
    props: {
        item: {
            type: Object as PropType<Sector>,
            required: true
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        isCanUpdate: {
            type: Boolean,
            default: false,
        },
        isCanDelete: {
            type: Boolean,
            default: false,
        },
        isCouriersLoading: {
            type: Boolean,
            default: false,
        },
        isCouriersFailed: {
            type: Boolean,
            default: false,
        },
        couriers: {
            type: Array as PropType<Array<Courier>>,
            default: () => []
        }
    },
    emits: ['delete', 'update', 'view', 'couriers-reload', 'courier-attached'],
    setup(props, { emit }) {
        const { havePermissions } = useAccount()
        const notifications = useNotifications()

        const selectedCourier = ref<number | null>(null)
        const isCourierAttaching = ref(false)
        const isCourierDetaching = ref(false)

        const onDelete = () => {
            if (props.disabled) return
            emit('delete', { ...props.item })
        }
        
        const onUpdate = () => {
            if (props.disabled) return
            emit('update', { ...props.item })
        }

        const onView = () => {
            if (props.disabled) return
            emit('view', { ...props.item })
        }

        const onReloadCouriers = () => {
            emit('couriers-reload')
        }

        const onAttachCourier = async (courierId: number) => {
            if (isCourierAttaching.value || isCourierDetaching.value || !isCanAttachCourier.value) return

            try {
                isCourierAttaching.value = true

                await sectorsAPI.attachCourier(props.item.id, courierId)

                notifications.notify({ type: 'success', title: 'Курьер успешно привязан к сектору' })
                emit('courier-attached')
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При привязке курьера к сектору произошла ошибка' })

                if (props.item.courier) {
                    selectedCourier.value = props.item.courier.id
                } else {
                    selectedCourier.value = null
                }
            } finally {
                isCourierAttaching.value = false
            }
        }

        const onDetachCourier = async () => {
            const answer = confirm('Вы действительно хотите отвязать курьера от сектора?')
            if (!answer) return

            try {
                isCourierDetaching.value = true

                await sectorsAPI.detachCourier(props.item.id)

                notifications.notify({ type: 'success', title: 'Курьер успешно отвязан' })
                emit('courier-attached')
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При отвязке курьера от сектора произошла ошибка' })
            } finally {
                isCourierDetaching.value = false
            }
        }

        onBeforeMount(() => {
            if (props.item.courier) {
                selectedCourier.value = props.item.courier.id
            }
        })

        const isCanAttachCourier = computed(() => havePermissions(['dispatcher.sector-attach-courier', 'all-root-permission']))

        const isCanDetachCourier = computed(() => {
            if (!havePermissions(['dispatcher.sector-detach-courier', 'all-root-permission'])) {
                return false
            }

            if (isCourierAttaching.value || isCourierDetaching.value) return false
            if (!selectedCourier.value) return false

            return true
        })

        return {
            selectedCourier,
            isCourierAttaching,
            isCourierDetaching,
            isCanAttachCourier,
            isCanDetachCourier,
            onDelete,
            onUpdate,
            onView,
            onReloadCouriers,
            onAttachCourier,
            onDetachCourier
        }
    }
})
